import { BaseService, UserService } from "core/service";
import { UserPersistence } from "core/persistence";
import BaseRepository from "core/repository/base.repository";
import RuntimeRepository from "core/repository/runtime.repository";
import CompanyRepository from "core/repository/company.repository";
import SaleRepository from "core/repository/sale.repository";
import { User } from "core/entity";
import Papa from "papaparse";
import Big from "big.js";
import axios from "axios"
import { createCookie, eraseCookie } from "../../util/use-cookie";

class UserRepository extends BaseRepository {

  syncable = false;

  getEntityIdentifier() {
    return "user";
  }

  resourceName() {
    return "usuários";
  }

  getPersistenceProvider() {
    return UserPersistence;
  }

  getServiceProvider() {
    return UserService;
  }

  async modelToEntity(model) {
    const entity = new User(model);

    const companyResult = await CompanyRepository.get(entity.company_uuid);

    if (companyResult.success) {
      entity.company = companyResult.data;
    }

    return entity;
  }

  async signIn(email, password) {
    this.setToken(null);
    const serviceResponse = await this.mService.signIn(
      email,
      password,
      "backoffice"
    );
    let data = null;

    if (serviceResponse.success) {
      data = serviceResponse.data;
      await this.persistCurrentUserToLocalStorage(data);
    } else if (serviceResponse.fatal) {
      // return await this._locally_authenticate(email, password);
    }

    return {
      data,
      success: serviceResponse.success,
      fatal: serviceResponse.fatal,
      error: serviceResponse.error
    };
  }

  async signOut() {
    await Promise.all([
      this.mRuntime.unset(RuntimeRepository.props.currentUser),
      this.mRuntime.unset(RuntimeRepository.props.token)
    ]);
    const current = process.env.REACT_APP_ENV
    eraseCookie(`enviroment_${current}`)
    BaseService.config({ authorizationToken: undefined });
  }

  async _locally_authenticate(email, password) {
    const user = await this.mPersistence.authenticate(email, password);

    return {
      success: !!user,
      fatal: !user,
      data: user
    };
  }

  async getLoggedInUser() {
    const token = await this.getTokenFromLocalStorage();

    if (token) {
      const tokenResponse = await this.validateToken(token);

      if (tokenResponse.success) {
        await this.persistCurrentUserToLocalStorage(tokenResponse.data);
      }

      return tokenResponse;
    }

    return { success: false, data: {} };
  }

  setToken(token) {
    return BaseService.config({ authorizationToken: token });
  }

  async validateToken(token) {
    this.setToken(token);
    return await this.mService.getCurrentUser();
  }

  async getTokenFromLocalStorage() {
    const token = await this.mRuntime.get(RuntimeRepository.props.token);
    return !!token ? token : false;
  }

  async getListOnlineByCompany(
    company_uuid,
    { filters = [], order = [], pagination = { page: 1, perPage: 25 } }
  ) {
    const result = await this.mService.fetch(company_uuid, {
      filters,
      order,
      pagination
    });

    if (!result.success) {
      return result;
    }

    const entitiesPromises = result.data.map(model =>
      this.modelToEntity(model)
    );

    result.data = await Promise.all(entitiesPromises);
    result.total = result.headers.total;
    result.perPage = result.headers["per-page"];

    return result;
  }

  async persistCurrentUserToLocalStorage(currentUser) {
    const operations = [
      this.mRuntime.set(RuntimeRepository.props.token, currentUser.token)
    ];

    if (!!currentUser.user) {
      const persistedUser = {
        ...currentUser.user,
        company: { ...currentUser.company }
      };

      operations.push(
        this.mRuntime.set(RuntimeRepository.props.currentUser, persistedUser)
      );
    }

    await Promise.all(operations);
  }

  async getReportCsvContent({ order, filters, saleFilters }) {
    const listResult = await this.mPersistence.getList({
      order,
      filters,
      pagination: false
    });

    if (!listResult.success) {
      return listResult;
    }

    const header = [
      "Nome do Vendedor",
      "Quantidade de Vendas",
      "Faturamento",
      "Custos",
      "Lucro Bruto"
    ];
    const data = await Promise.all(
      listResult.data.map(user => this._userToReportCsvLine(user, saleFilters))
    );
    const content = Papa.unparse({ fields: header, data: data });

    return { success: true, data: content };
  }

  async _userToReportCsvLine(model, saleFilters) {
    const user = await this.modelToEntity(model);
    const filters = await saleFilters(user);
    const repositoryResult = await SaleRepository.getReportSummary(filters);

    if (repositoryResult.success) {
      let absoluteProfit = "0.00";
      if (repositoryResult.data.profit) {
        absoluteProfit = Big(repositoryResult.data.profit)
          .div(100)
          .toFixed(2);
      }
      return [
        user.name,
        repositoryResult.data.count,
        Big(repositoryResult.data.revenues)
          .div(100)
          .toFixed(2),
        Big(repositoryResult.data.costs)
          .div(100)
          .toFixed(2),
        absoluteProfit
      ];
    } else {
      return [user.name, "-", "-", "-", "-"];
    }
  }

  async getUsers({ filters = [], order = [], pagination = {} }) {
    const currentUser = await this.mRuntime.get(
      RuntimeRepository.props.currentUser
    );
    const serviceResult = await this.mService.fetch(currentUser.company_uuid, {
      filters,
      order,
      pagination
    });

    if (serviceResult.success) {
      return {
        success: true,
        data: serviceResult.data,
        total: serviceResult.headers["total"],
        perPage: serviceResult.headers["per-page"]
      };
    }

    return {
      success: false,
      fatal: serviceResult.fatal,
      errors: serviceResult.errors
    };
  }

  async getSupportUsers({ filters = [] }) {
    const serviceResult = await this.mService.fetchWithoutCompanyUuid({
      filters
    });

    if (serviceResult.success) {
      return {
        success: true,
        data: serviceResult.data,
        total: serviceResult.headers["total"]
      };
    }

    return {
      success: false,
      fatal: serviceResult.fatal,
      errors: serviceResult.errors
    };
  }

  async deleteUser(uuid) {
    const currentUser = await this.mRuntime.get(
      RuntimeRepository.props.currentUser
    );
    return await this.update(uuid, {
      company_uuid: currentUser.company_uuid,
      deleted: true
    });
  }

  async deleteUserByCompany(uuid, companyUuid, deleted) {
    return await this.update(uuid, { company_uuid: companyUuid, deleted });
  }

  async recoverPassword(email) {
    return await this.mService.recoverPassword(email);
  }

  async getEnviromentsByUser(email) {
    const ENVIROMENTS_API = process.env.REACT_APP_ENVIROMENTS_API_URL
    const ENVIROMENTS_TOKEN = process.env.REACT_APP_ENVIROMENTS_TOKEN

    try {
      const enviroments = await axios.get(
        `${ENVIROMENTS_API}/api/v1/user_domains`,
         {
           headers: {
             "Access-Control-Allow-Origin": "*",
             authorization: `Bearer ${ENVIROMENTS_TOKEN}`
           },
           params: {
             username: email,
           },
         }
       ); 
       return {
        success: true,
        data: enviroments.data
      }
    } catch(e) {
      return {
        success: false,
        data: {}
      }
    }
  }

  async persistCurrentEnviromentToCookies(enviroment) {
    const promise = new Promise((resolve, reject) => {
      const current = process.env.REACT_APP_ENV
      createCookie(`enviroment_${current}`, enviroment)
      resolve()
    });
   return await promise
  }
}

export default new UserRepository();



// WEBPACK FOOTER //
// ./src/core/repository/user.repository.js